/* GASSISA
=======================================================================================
* Descripción: Modal para apertura de caja registradora
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 23/03/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 24/03/2026 | Lic. José Luis Aragón Cervantes  | Agregar desglose monedas/billetes
*      3       | 24/03/2026 | Lic. José Luis Aragón Cervantes  | Agregar impresión ticket apertura
*      4       | 19/10/2026 | Lic. José Luis Aragón Cervantes  | Ctrl+Alt+F8 ejecuta apertura manual de cajón de dinero
*      5       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Botón manual de cajón; retiro de atajo global
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import React, { Fragment, useState, useEffect, useRef } from "react";
import { Modal, Box, Typography, Button, Divider } from "@mui/material";
import { NumericFormat } from 'react-number-format';
import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { apiAperturaCaja } from "../../settings/ApiConfig";
import { useVentas } from "../../context/Ventas/VentasContext";
import SizeSoftXLoadingProcess from "../leyout/SizeSoftXLoadingProcess";

const listaTiposMonedas = [
    { "TIPO": "MONEDA", "VALOR": 0.50 },
    { "TIPO": "MONEDA", "VALOR": 1 },
    { "TIPO": "MONEDA", "VALOR": 2 },
    { "TIPO": "MONEDA", "VALOR": 5 },
    { "TIPO": "MONEDA", "VALOR": 10 },
    { "TIPO": "BILLETE", "VALOR": 20 },
    { "TIPO": "BILLETE", "VALOR": 50 },
    { "TIPO": "BILLETE", "VALOR": 100 },
    { "TIPO": "BILLETE", "VALOR": 200 },
    { "TIPO": "BILLETE", "VALOR": 500 },
    { "TIPO": "BILLETE", "VALOR": 1000 }
];

const modalStyle = {
    position: 'fixed',
    top: '2%',
    left: '25%',
    width: '50vw',
    maxWidth: '90vw',
    maxHeight: '95vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
    overflowY: 'auto',
};

export default function ModalAperturaCaja(props) {
    const { openModal = false, onClose, onSuccess, caja } = props;
    
    const { usuarioData, parametrosCajaForm } = useSesion();
    const { handleOpenAlert } = useLayout();
    const { PostRequest } = useRequest();
    const { handleObtenerResumenTotalesCaja, handleAbrirCajonDinero } = useVentas();

    const [internalOpen, setInternalOpen] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [montoInicial, setMontoInicial] = useState(0);
    const [observaciones, setObservaciones] = useState("");
    const [loading, setLoading] = useState(false);
    const [listadoMonedas, setListadoMonedas] = useState(
        listaTiposMonedas.map(m => ({ ...m, Cantidad: 0 }))
    );
    const [totalConteo, setTotalConteo] = useState(0);
    
    const primerInputRef = useRef(null);
    const cantidadInputsRef = useRef([]);
    const observacionesInputRef = useRef(null);
    const abrirCajaButtonRef = useRef(null);

    useEffect(() => {
        cantidadInputsRef.current = [];
    }, [listadoMonedas]);

    const handleKeyDown = (e, index, totalCampos) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            if (e.key === 'Enter') {
                e.preventDefault();
            }
            if (index < totalCampos - 1) {
                const nextInput = cantidadInputsRef.current[index + 1];
                if (nextInput) {
                    nextInput.focus();
                    nextInput.select();
                }
            } else if (index === totalCampos - 1) {
                if (observacionesInputRef.current) {
                    observacionesInputRef.current.focus();
                    observacionesInputRef.current.select();
                }
            }
        }
    };

    const handleObservacionesKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            if (abrirCajaButtonRef.current) {
                abrirCajaButtonRef.current.focus();
            }
        }
    };

    const handleOnFocus = (e) => {
        e.target.select();
    };

    useEffect(() => {
        if (openModal) {
            setInternalOpen(true);
            setMontoInicial(0);
            setObservaciones("");
            setListadoMonedas(listaTiposMonedas.map(m => ({ ...m, Cantidad: 0 })));
            setTimeout(() => setModalVisible(true), 50);
            setTimeout(() => {
                if (primerInputRef.current) {
                    primerInputRef.current.focus();
                    primerInputRef.current.select();
                }
            }, 100);
        } else {
            setModalVisible(false);
            setTimeout(() => setInternalOpen(false), 300);
        }
    }, [openModal]);

    useEffect(() => {
        const total = listadoMonedas.reduce((acc, item) => acc + (item.Cantidad * item.VALOR), 0);
        setTotalConteo(total);
    }, [listadoMonedas]);

    const handleUpdateListaMonedas = (tipo, valor, cantidad) => {
        const updatedList = listadoMonedas.map((item) =>
            item.TIPO === tipo && item.VALOR === valor
                ? { ...item, Cantidad: parseFloat(cantidad) || 0 }
                : item
        );
        setListadoMonedas(updatedList);
    };

    const imprimirTicketApertura = async (dataApertura, resumenCorte) => {
        const formData = new FormData();
        const textEncoder = new TextEncoder();

        let ticketParts = [
            textEncoder.encode('\x1B\x40'),
        ];

        const cab = resumenCorte || {};

        ticketParts.push(
            textEncoder.encode('\x1B\x61\x01\x1B\x21\x11' + 'APERTURA DE CAJA\n'),
            textEncoder.encode('\x1B\x32'),
            textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
            textEncoder.encode('Caja: ' + (caja || "") + '\n'),
            textEncoder.encode('Usuario: ' + (usuarioData?.Usuario || "") + '\n'),
            textEncoder.encode('Fecha: ' + new Date().toLocaleString('es-MX') + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n'),
        );

        // Solo monto de apertura (sin totales de ventas)
        ticketParts.push(
            textEncoder.encode('\n--- MONTO APERTURA ---\n'),
            textEncoder.encode('Monto Inicial:   ' + formatCurrency(dataApertura.montoinicial || 0).padStart(15) + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n'),
        );

        const monedas = dataApertura.listacantidadmonedasbilletes || [];
        if (monedas.length > 0) {
            ticketParts.push(textEncoder.encode('DESGLOSE EFECTIVO APERTURA:\n'));
            ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n'));
            
            monedas.forEach(m => {
                if (m.cantidad > 0) {
                    const subtotal = m.cantidad * m.valor;
                    const label = m.tipo === 'MONEDA' ? 'Mda' : 'Bll';
                    ticketParts.push(textEncoder.encode(
                        `${label} $${m.valor.toFixed(2).padStart(6)} x ${m.cantidad.toString().padStart(3)} = $${subtotal.toFixed(2).padStart(8)}\n`
                    ));
                }
            });
            
            ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n'));
        }

        if (observaciones) {
            ticketParts.push(textEncoder.encode('\nObs: ' + observaciones + '\n'));
        }

        ticketParts.push(
            textEncoder.encode('\n\n'),
            textEncoder.encode('\x1B\x61\x01' + 'Caja abierta exitosamente!\n\n\n'),
            textEncoder.encode('\x1B\x64\x06'),
            textEncoder.encode('\x1D\x56\x00')
        );

        const totalLength = ticketParts.reduce((sum, part) => sum + part.length, 0);
        const finalTicket = new Uint8Array(totalLength);
        let offset = 0;
        ticketParts.forEach(part => {
            finalTicket.set(part, offset);
            offset += part.length;
        });

        formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
        formData.append("printerName", parametrosCajaForm?.nombreimpresoraventas?.trim() || "");

        try {
            const response2 = await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });

            if (!response2.ok) {
                handleOpenAlert("Error en envío de impresión:", response2.statusText);
            }
        } catch (error) {
            handleOpenAlert("Error en impresión:", error.message);
        }
    };

    const handleAperturaCaja = async () => {
        if (!caja || caja === 'CAJA-DEFAULT') {
            handleOpenAlert("Seleccione una caja vélida", "warning");
            return;
        }

        if (totalConteo <= 0) {
            handleOpenAlert("El monto inicial debe ser mayor a 0", "warning");
            return;
        }

        setLoading(true);
        try {
            const listaMonedasFormateada = listadoMonedas.map(m => ({
                tipo: m.TIPO,
                valor: m.VALOR,
                cantidad: m.Cantidad
            }));

            const data = {
                id: 0,
                caja: caja,
                fechaapertura: new Date().toLocaleString('sv').replace(' ', 'T'),
                montoinicial: totalConteo,
                usuario: usuarioData.Usuario,
                status: "ABIERTA",
                observaciones: observaciones,
                listacantidadmonedasbilletes: listaMonedasFormateada
            };

            const response = await PostRequest({
                url: apiAperturaCaja,
                body: data,
            });

            if (response.hasError) {
                handleOpenAlert(response.message, "error");
                return;
            }

            handleOpenAlert("Caja abierta correctamente", "success");

            // Obtener resumen de totales actualizado para el ticket
            const resumen = await handleObtenerResumenTotalesCaja(
                new Date().toISOString().split('T')[0],
                caja,
                usuarioData.Usuario
            );

            await imprimirTicketApertura(data, resumen);
            
            if (onSuccess) {
                onSuccess(response.result);
            }
            
            if (onClose) {
                onClose();
            }
        } catch (error) {
            handleOpenAlert("Error al abrir caja: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(value || 0);
    };

    return (
        <Modal
            open={internalOpen}
            onClose={onClose}
            closeAfterTransition
        >
            <Box sx={{ ...modalStyle, transform: modalVisible ? 'scale(1)' : 'scale(0.8)', opacity: modalVisible ? 1 : 0, transition: 'all 0.3s ease-in-out' }}>
                <Fragment>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Apertura de Caja
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Caja: <strong>{caja}</strong>
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                        Desglose de Efectivo
                    </Typography>

                    <Box sx={{ maxHeight: '100%', overflowY: 'auto', mb: 2 }}>
                        {listadoMonedas.map((moneda, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Typography sx={{ minWidth: '80px' }}>
                                    {moneda.TIPO} ${moneda.VALOR}
                                </Typography>
                                <NumericFormat
                                    inputRef={(el) => {
                                        if (index === 0) primerInputRef.current = el;
                                        cantidadInputsRef.current[index] = el;
                                    }}
                                    value={moneda.Cantidad}
                                    thousandSeparator={true}
                                    decimalScale={0}
                                    fixedDecimalScale
                                    customInput={TextField}
                                    label="Cantidad"
                                    sx={{ width: '150px' }}
                                    size="small"
                                    onValueChange={(values) => handleUpdateListaMonedas(moneda.TIPO, moneda.VALOR, values.value)}
                                    onKeyDown={(e) => handleKeyDown(e, index, listadoMonedas.length)}
                                    onFocus={handleOnFocus}
                                />
                                <Typography sx={{ minWidth: '100px', textAlign: 'right' }}>
                                    = {formatCurrency(moneda.Cantidad * moneda.VALOR)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Total Apertura:
                        </Typography>
                        <Typography variant="h5" color="primary">
                            {formatCurrency(totalConteo)}
                        </Typography>
                    </Box>

                    <TextField autoComplete="off"
                        inputRef={observacionesInputRef}
                        label="Observaciones"
                        multiline
                        rows={2}
                        fullWidth
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                        onKeyDown={handleObservacionesKeyDown}
                        onFocus={handleOnFocus}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={() => handleAbrirCajonDinero()}
                            disabled={loading}
                        >
                            Abrir cajón
                        </Button>
                        <Button 
                            variant="outlined" 
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button 
                            ref={abrirCajaButtonRef}
                            variant="contained" 
                            color="primary"
                            onClick={handleAperturaCaja}
                            disabled={loading || totalConteo <= 0 || !caja || caja === 'CAJA-DEFAULT'}
                        >
                            {loading ? "Abriendo..." : "Abrir Caja"}
                        </Button>
                    </Box>
                </Fragment>
                <SizeSoftXLoadingProcess
                    open={loading}
                    message="Procesando apertura de caja, por favor espere..."
                />
            </Box>
        </Modal>
    );
}
